import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, FileText, Activity, TestTube, Brain } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";

export function DashboardTotals() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    patients: 0,
    contents: 0,
    records: 0,
    results: 0,
    analyses: 0,
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Pacientes
      const { count: patientsCount } = await supabase
        .from("patients_onboarding")
        .select("*", { count: "exact", head: true })
        .eq("owner_id", user.id);

      // Conteúdos (smartfile sessions)
      const { count: contentsCount } = await supabase
        .from("smartfile_sessions")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id);

      // Resultados (lab results) - buscar IDs dos pacientes primeiro
      const { data: patientIds } = await supabase
        .from("patients_onboarding")
        .select("id")
        .eq("owner_id", user.id);

      const patientIdList = patientIds?.map((p) => p.id) || [];

      // Registros: contar de patient_health_records + structured_records das sessões
      let recordsCount = 0;

      // 1. Contar registros salvos em patient_health_records
      if (patientIdList.length > 0) {
        const { count: healthRecordsCount, error: healthRecordsError } = await supabase
          .from("patient_health_records")
          .select("*", { count: "exact", head: true })
          .in("patient_id", patientIdList);

        if (!healthRecordsError && healthRecordsCount) {
          recordsCount += healthRecordsCount;
          console.log("Health records count:", healthRecordsCount);
        } else if (healthRecordsError) {
          console.error("Error loading health records:", healthRecordsError);
          // Fallback: tentar por professional_id
          const { count: countByProf } = await supabase
            .from("patient_health_records")
            .select("*", { count: "exact", head: true })
            .eq("professional_id", user.id);
          if (countByProf) recordsCount += countByProf || 0;
        }
      } else {
        // Se não há pacientes, tentar por professional_id
        const { count: countByProf } = await supabase
          .from("patient_health_records")
          .select("*", { count: "exact", head: true })
          .eq("professional_id", user.id);
        if (countByProf) recordsCount += countByProf || 0;
      }

      // 2. Contar structured_records das sessões dos pacientes
      if (patientIdList.length > 0) {
        const { data: sessions, error: sessionsError } = await supabase
          .from("smartfile_sessions")
          .select("structured_records, patient_id")
          .in("patient_id", patientIdList)
          .not("structured_records", "is", null);

        if (!sessionsError && sessions) {
          let structuredRecordsCount = 0;
          interface SessionRecord {
            is_main_record?: boolean | string;
            type?: string;
          }
          sessions.forEach((session) => {
            const structuredRecords = session.structured_records;
            if (structuredRecords) {
              let records: SessionRecord[] = [];
              if (Array.isArray(structuredRecords)) {
                records = structuredRecords as SessionRecord[];
              } else if (typeof structuredRecords === 'string') {
                try {
                  const parsed = JSON.parse(structuredRecords);
                  if (Array.isArray(parsed)) records = parsed;
                } catch {
                  records = [];
                }
              }

              // Contar apenas registros válidos (excluir main_record)
              const validRecords = records.filter((r) => {
                if (typeof r !== 'object' || r === null || !('type' in r)) return false;
                // Excluir main_record
                if (r.is_main_record === true || r.is_main_record === 'true') return false;
                return true;
              });

              structuredRecordsCount += validRecords.length;
            }
          });
          recordsCount += structuredRecordsCount;
          console.log("Structured records count from sessions:", structuredRecordsCount);
        } else if (sessionsError) {
          console.error("Error loading structured records from sessions:", sessionsError);
        }
      }

      console.log("Total records count:", recordsCount);

      const { count: resultsCount } = await supabase
        .from("lab_results_onboarding")
        .select("*", { count: "exact", head: true })
        .in("patient_id", patientIdList);

      // Análises IA (global analysis cache)
      const { count: analysesCount } = await supabase
        .from("ai_global_analysis_cache")
        .select("*", { count: "exact", head: true })
        .in("patient_id", patientIdList);

      setStats({
        patients: patientsCount || 0,
        contents: contentsCount || 0,
        records: recordsCount || 0,
        results: resultsCount || 0,
        analyses: analysesCount || 0,
      });
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  const totals = [
    {
      icon: Users,
      label: "Pacientes",
      value: stats.patients,
      color: "text-primary",
      bgColor: "bg-primary/10",
      onClick: () => navigate("/app/pacientes"),
    },
    {
      icon: FileText,
      label: "Conteúdos",
      value: stats.contents,
      color: "text-primary",
      bgColor: "bg-primary/10",
    },
    {
      icon: Activity,
      label: "Registros",
      value: stats.records,
      color: "text-primary",
      bgColor: "bg-primary/10",
    },
    {
      icon: TestTube,
      label: "Resultados",
      value: stats.results,
      color: "text-primary",
      bgColor: "bg-primary/10",
    },
    {
      icon: Brain,
      label: "Análises IA",
      value: stats.analyses,
      color: "text-primary",
      bgColor: "bg-primary/10",
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
      {totals.map((total, index) => (
        <Card
          key={index}
          className={`border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-lg ${
            total.onClick ? "cursor-pointer" : ""
          }`}
          onClick={total.onClick}
        >
          <CardContent className="p-4">
            <div className="flex flex-col items-center text-center gap-2">
              <div className={`p-2.5 rounded-lg ${total.bgColor}`}>
                <total.icon className={`h-5 w-5 ${total.color}`} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{total.label}</p>
                <p className="text-2xl font-bold">{total.value}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
